<template>
  <div class="website-builder">
    <v-app-bar app flat color="white" height="56" elevation="1">
      <v-toolbar-title
        class="text-h6 font-weight-regular grey--text text--darken-2 ml-2"
      >
        {{ bookingEngineSetting?.site_name || $t('Website Builder') }}
      </v-toolbar-title>
    </v-app-bar>

    <v-navigation-drawer permanent class="grey lighten-5" width="240" app>
      <v-list nav dense class="pa-2">
        <v-list-item-group v-model="currentTab" color="primary">
          <!-- Settings Tabs -->
          <v-list-item
            v-for="(tab, index) in settingsTabs"
            :key="'setting-' + index"
            :value="tab.route"
            link
            class="mb-1"
          >
            <v-list-item-icon class="mr-3">
              <v-icon>{{ tab.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-body-1 font-weight-medium">
                {{ $t(tab.title) }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Divider -->
          <v-divider class="my-4 mx-2"></v-divider>

          <v-subheader class="text-uppercase font-weight-medium pl-4">{{
            $t('Pages')
          }}</v-subheader>

          <!-- Page Tabs -->
          <div v-for="(tab, index) in pageTabs" :key="'page-' + index">
            <v-list-item
              :value="tab.route"
              link
              class="mb-1"
              @click="currentTab = tab.route"
            >
              <v-list-item-icon class="mr-3">
                <v-icon :color="currentTab === tab.route ? 'primary' : ''">{{
                  tab.icon
                }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content class="pr-0">
                <v-list-item-title class="d-flex align-center">
                  <v-icon
                    v-if="!tab.alwaysActive"
                    :color="getPageActiveState(tab.route) ? 'primary' : 'grey'"
                    class="mr-2"
                    small
                  >
                    mdi-eye
                  </v-icon>
                  {{ $t(tab.title) }}
                  <v-spacer></v-spacer>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>

    <v-main class="main-content grey lighten-4">
      <div class="content-wrapper">
        <v-container fluid class="pa-6">
          <v-row>
            <v-col cols="12" class="py-0">
              <keep-alive>
                <component
                  :is="currentTabComponent"
                  :booking-engine-setting="bookingEngineSetting"
                  :listings="listings"
                  @update:setting="updateSetting"
                />
              </keep-alive>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <div class="actions-footer white">
        <v-container fluid class="pa-3">
          <v-row>
            <v-col cols="12" class="d-flex justify-end align-center py-1">
              <v-btn text large class="mr-3" @click="$emit('close')">
                {{ $t('Cancel') }}
              </v-btn>
              <v-btn
                large
                color="primary"
                elevation="1"
                :loading="loading"
                @click="save"
              >
                {{ $t('Save Changes') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-main>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import GeneralSettings from './tabs/general-settings.vue'
import LandingPage from './tabs/landing-page.vue'
import Properties from './tabs/properties.vue'
import BusinessInfo from './tabs/business-info.vue'
import BookingSettings from './tabs/booking-settings.vue'
import CustomContent from './tabs/custom-content.vue'
import AboutPage from './tabs/about-page.vue'
import TermsPage from './tabs/terms-page.vue'
import PrivacyPage from './tabs/privacy-page.vue'
import ContactFormPage from './tabs/contact-form-page.vue'
import ExitScreenSettings from './tabs/exit-screen-settings.vue'
import Destinations from './tabs/destinations.vue'

/**
 * Website Builder main component
 *
 * 2024-05-24: Added Destinations tab support and fixed saving of destination_cities from Destinations tab
 */

export default {
  name: 'WebsiteBuilder',

  components: {
    GeneralSettings,
    LandingPage,
    Properties,
    BusinessInfo,
    BookingSettings,
    CustomContent,
    AboutPage,
    TermsPage,
    PrivacyPage,
    ContactFormPage,
    ExitScreenSettings,
    Destinations,
  },

  props: {
    currentBookingEngineSetting: {
      type: Object,
      default: null,
    },
    listings: {
      type: Array,
      required: true,
    },
  },

  data() {
    const initialSetting = {
      site_name: '',
      pages: {
        header: {
          logo: null,
          background_color: '#FFFFFF',
          text_color: '#000000',
        },
        favorites: {
          enabled: true,
          background_color: '#FFFFFF',
          text_color: '#000000',
        },
        footer: {
          enabled: true,
          background_color: '#FFFFFF',
          text_color: '#000000',
        },
        about: {
          active: false,
          header: {
            title: '',
            sub_title: '',
            story: '',
          },
          header_image: null,
          team_members: [],
        },
        landing: {
          active: true,
        },
        terms: {
          active: false,
          content: '',
        },
        privacy: {
          active: false,
          content: '',
        },
        contact: {
          active: false,
        },
      },
      theme: {
        primary: '#1976D2',
        secondary: '#424242',
        accent: '#82B1FF',
      },
      listing_nickname: '',
      active: true,
      injected_scripts: '',
      min_ahead: 0,
      show_infants: false,
      allow_bank_payments: false,
      show_fees_breakdown: false,
      custom_markup: '',
      cancellation_policy: '',
      custom_terms_of_use: '',
      business: { email: '', phone: '', name: '', address: '' },
      social_links: {
        facebook: '',
        google: '',
        instagram: '',
        linkedin: '',
        website: '',
        booking_site: '',
      },
      show_whatsapp_link: false,
      whitelist: [],
      blacklist: [],
      featured_listings: [],
      filter_by_region: false,
      filter_by_neighborhood: false,
      site_title: '',
      custom_domain: '',
      site_type: 'vacation_rental',
      analytics: { google: '', gtm: '', pixel: '' },
      show_cookie_consent: false,
      bg_image: null,
      logo: null,
      favicon: null,
      destination_cities: [],
      exitScreen: {
        enabled: false,
        title: 'Before You Go...',
        subtitle: 'Get 10% off your first dream vacation rental!',
        image: null,
        offerDiscount: true,
        discountSubtitle: 'Use this code at checkout:',
        couponCode: '10off',
        askForEmail: false,
      },
    }

    return {
      loading: false,
      currentTab: 'general-settings',
      bookingEngineSetting: this.currentBookingEngineSetting || initialSetting,
      settingsTabs: [
        {
          title: 'General Settings',
          icon: 'mdi-cog-outline',
          route: 'general-settings',
        },
        {
          title: 'Properties',
          icon: 'mdi-domain',
          route: 'properties',
        },
        {
          title: 'Business Info',
          icon: 'mdi-office-building-outline',
          route: 'business-info',
        },
        {
          title: 'Booking Settings',
          icon: 'mdi-calendar-check-outline',
          route: 'booking-settings',
        },
        {
          title: 'Custom Content',
          icon: 'mdi-xml',
          route: 'custom-content',
        },
      ],
      pageTabs: [
        {
          title: 'Landing Page',
          icon: 'mdi-home-outline',
          route: 'landing-page',
          alwaysActive: true,
        },
        {
          title: 'About',
          icon: 'mdi-information-outline',
          route: 'about-page',
          alwaysActive: false,
        },
        {
          title: 'Contact Form',
          icon: 'mdi-email-outline',
          route: 'contact-form-page',
          alwaysActive: false,
        },
        {
          title: 'Terms & Conditions',
          icon: 'mdi-file-document-outline',
          route: 'terms-page',
          alwaysActive: false,
        },
        {
          title: 'Privacy Policy',
          icon: 'mdi-shield-lock-outline',
          route: 'privacy-page',
          alwaysActive: false,
        },
        {
          title: 'Destinations',
          icon: 'mdi-map-marker-multiple-outline',
          route: 'destinations',
          alwaysActive: false,
        },
        {
          title: 'Exit Screen',
          icon: 'mdi-exit-run',
          route: 'exit-screen-settings',
          alwaysActive: false,
        },
      ],
    }
  },

  computed: {
    ...mapGetters(['listingCities', 'currentUser']),
    editMode() {
      return !!this.currentBookingEngineSetting?.id
    },
    currentTabComponent() {
      const componentMap = {
        'general-settings': 'GeneralSettings',
        'landing-page': 'LandingPage',
        'about-page': 'AboutPage',
        'contact-form-page': 'ContactFormPage',
        'terms-page': 'TermsPage',
        'privacy-page': 'PrivacyPage',
        properties: 'Properties',
        'business-info': 'BusinessInfo',
        'booking-settings': 'BookingSettings',
        'custom-content': 'CustomContent',
        'exit-screen-settings': 'ExitScreenSettings',
        destinations: 'Destinations',
      }
      return componentMap[this.currentTab] || 'GeneralSettings'
    },
  },

  methods: {
    updateSetting(newData) {
      this.bookingEngineSetting = {
        ...this.bookingEngineSetting,
        ...newData,
      }
    },

    async save() {
      this.loading = true
      try {
        const url = this.editMode
          ? `/api/booking-engine-settings/${this.currentBookingEngineSetting.id}`
          : '/api/booking-engine-settings'
        const method = this.editMode ? 'patch' : 'post'

        const dataToSend = {
          ...this.bookingEngineSetting,
          pages: {
            ...this.bookingEngineSetting.pages,
          },
          exitScreen: {
            ...this.bookingEngineSetting.exitScreen,
          },
        }

        await axios({
          method,
          url,
          data: dataToSend,
        })

        this.$store.dispatch('getBookingEngineSettings', { active_only: true })
        this.$store.dispatch('snackbar/showSnackbar', {
          message: this.$t('Changes saved successfully'),
          color: 'success',
        })

        this.$emit('close')
      } catch (error) {
        console.error('Error saving booking engine settings:', error)
        this.$store.dispatch('snackbar/showSnackbar', {
          message: this.$t('Failed to save changes'),
          color: 'error',
        })
      } finally {
        this.loading = false
      }
    },

    getPageActiveState(route) {
      const section = this.getPageSection(route)
      if (section === 'exitScreen') {
        return this.bookingEngineSetting?.exitScreen?.enabled || false
      }
      return this.bookingEngineSetting?.pages?.[section]?.active || false
    },

    setPageActiveState(route, value) {
      const section = this.getPageSection(route)
      if (section) {
        if (!this.bookingEngineSetting.pages) {
          this.bookingEngineSetting.pages = {}
        }

        if (!this.bookingEngineSetting.pages[section]) {
          this.bookingEngineSetting.pages[section] = {}
        }

        this.bookingEngineSetting.pages[section].active = value
      }
    },

    getPageSection(route) {
      const sectionMap = {
        'landing-page': 'landing',
        'about-page': 'about',
        'terms-page': 'terms',
        'privacy-page': 'privacy',
        'contact-form-page': 'contact',
        'exit-screen-settings': 'exitScreen',
      }
      return sectionMap[route]
    },

    updatePageActive(route) {
      event?.stopPropagation()

      const section = this.getPageSection(route)
      if (section) {
        if (section === 'exitScreen') {
          this.bookingEngineSetting.exitScreen.enabled =
            !this.bookingEngineSetting.exitScreen.enabled

          if (
            !this.bookingEngineSetting.exitScreen.enabled &&
            this.currentTab === route
          ) {
            this.currentTab = 'general-settings'
          }
          return
        }

        if (!this.bookingEngineSetting.pages) {
          this.bookingEngineSetting.pages = {}
        }

        if (!this.bookingEngineSetting.pages[section]) {
          this.bookingEngineSetting.pages[section] = { active: false }
        }

        this.bookingEngineSetting.pages[section].active =
          !this.bookingEngineSetting.pages[section].active

        if (
          !this.bookingEngineSetting.pages[section].active &&
          this.currentTab === route
        ) {
          this.currentTab = 'general-settings'
        }
      }
    },
  },

  watch: {
    currentBookingEngineSetting: {
      handler(newVal, oldVal) {
        if (newVal?.id !== oldVal?.id) {
          this.bookingEngineSetting = newVal || {
            site_name: '',
            pages: {
              header: {
                logo: null,
                background_color: '#FFFFFF',
                text_color: '#000000',
              },
              favorites: {
                enabled: true,
                background_color: '#FFFFFF',
                text_color: '#000000',
              },
              footer: {
                enabled: true,
                background_color: '#FFFFFF',
                text_color: '#000000',
              },
              about: {
                active: false,
                header: {
                  title: '',
                  sub_title: '',
                  story: '',
                },
                header_image: null,
                team_members: [],
              },
              landing: {
                active: true,
              },
              terms: {
                active: false,
                content: '',
              },
              privacy: {
                active: false,
                content: '',
              },
              contact: {
                active: false,
              },
            },
            theme: {
              primary: '#1976D2',
              secondary: '#424242',
              accent: '#82B1FF',
            },
            listing_nickname: '',
            active: true,
            injected_scripts: '',
            min_ahead: 0,
            show_infants: false,
            allow_bank_payments: false,
            show_fees_breakdown: false,
            custom_markup: '',
            cancellation_policy: '',
            custom_terms_of_use: '',
            business: { email: '', phone: '', name: '', address: '' },
            social_links: {
              facebook: '',
              google: '',
              instagram: '',
              linkedin: '',
              website: '',
              booking_site: '',
            },
            show_whatsapp_link: false,
            whitelist: [],
            blacklist: [],
            featured_listings: [],
            filter_by_region: false,
            filter_by_neighborhood: false,
            site_title: '',
            custom_domain: '',
            site_type: 'vacation_rental',
            analytics: { google: '', gtm: '', pixel: '' },
            show_cookie_consent: false,
            bg_image: null,
            logo: null,
            favicon: null,
            destination_cities: [],
            exitScreen: {
              enabled: false,
              title: 'Before You Go...',
              subtitle: 'Get 10% off your first dream vacation rental!',
              image: null,
              offerDiscount: true,
              discountSubtitle: 'Use this code at checkout:',
              couponCode: '10off',
              askForEmail: false,
            },
          }
        }
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
.website-builder {
  display: flex;
  height: 100vh;
}

.main-content {
  overflow-y: auto;
  height: calc(100vh - 56px);
}

.content-wrapper {
  padding-bottom: 72px;
  min-height: 100%;
}

.actions-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  position: fixed;
  bottom: 0;
  right: 0;
  left: 240px;
  z-index: 4;
  height: 72px;
  background-color: white;
}

.v-list-item--active::before {
  opacity: 0.1 !important;
}
.v-list-item--active .v-list-item__title {
  font-weight: 600 !important;
}
</style>
